import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import classes from "./artistProfile.module.css";

// Import components
import Footer from "../../components/footer";
import ArtistPhotoCarousel from "./ArtistPhotoCarousel";
import ArtistVideoCarousel from "./ArtistVideoCarousel";
import { getProfile } from "../../redux/slices/artistProfileSlice";

const ArtistPreviewProfile = () => {
  const { username } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // const { artistByUsername, loading, error } = useSelector(
  //   (state) => state.artists
  // );

  const {
    artistProfile: artistByUsername,
    profileLoading: loading,
    profileError: error,
  } = useSelector((state) => state.artistProfile);

  const [activeTab, setActiveTab] = useState("about");
  const router = useNavigate();
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(getProfile());

    // Add scroll listener for sticky header
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsHeaderSticky(true);
      } else {
        setIsHeaderSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [dispatch, username]);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <div className={classes.premiumLoader}>
          <div className={classes.loaderRing}></div>
          <div className={classes.loaderRing}></div>
          <div className={classes.loaderRing}></div>
        </div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className={classes.loadingText}
        >
          Preparing artist profile...
        </motion.p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.errorContainer}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className={classes.errorContent}
        >
          <div className={classes.errorIcon}>!</div>
          <h3>Oops! Something went wrong</h3>
          <p>{error}</p>
          <button className={classes.returnButton} onClick={() => navigate(-1)}>
            Go Back
          </button>
        </motion.div>
      </div>
    );
  }

  if (!artistByUsername) {
    return (
      <div className={classes.errorContainer}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className={classes.errorContent}
        >
          <div className={classes.errorIcon}>?</div>
          <h3>Artist Not Found</h3>
          <p>We couldn't find the artist you're looking for.</p>
          <button className={classes.returnButton} onClick={() => navigate(-1)}>
            Go Back
          </button>
        </motion.div>
      </div>
    );
  }

  const toggleBookingModal = () => {
    router(`/booking/${username}`);
  };

  const capitalize = (str) =>
    `${str?.charAt(0)?.toUpperCase()}${str?.slice(1)?.toLowerCase()}`;

  // Extract artist data
  const {
    first_name,
    last_name,
    username: artistUsername,
    categories,
    photos,
    videos,
    available_locations,
    about,
  } = artistByUsername;

  const fullName =
    first_name !== undefined ? `${first_name} ${last_name}` : "Loading...";

  const category = categories?.[0]?.name || "Not specified";
  const genre = categories?.[0]?.genre
    ? categories[0].genre
        .map((g) =>
          g.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
        )
        .join(", ")
    : "Not specified";
  const location =
    available_locations?.length > 0
      ? available_locations.join(", ")
      : "Location not specified";

  return (
    <>
      <div className={`${classes.profileContainer} `}>
        {/* Sticky Header */}

        {/* Hero Section */}
        <div className={classes.heroSection}>
          <div className={classes.heroOverlay}></div>
          <motion.div
            className={classes.profileHeader}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className={classes.headerContent}>
              <motion.h1
                className={classes.artistName}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                style={{ textTransform: "capitalize" }}
              >
                {fullName || "Not specified"}
              </motion.h1>

              <motion.div
                className={classes.artistMeta}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className={classes.metaItem}>
                  <span className={classes.metaIcon}>📍</span>
                  <span>{location}</span>
                </div>

                <div className={classes.metaItem}>
                  <span className={classes.metaIcon}>🎭</span>
                  <span>{genre}</span>
                </div>

                <div className={classes.metaItem}>
                  <span className={classes.metaIcon}>⭐</span>
                  <span>Verified Artist</span>
                </div>

                <div className={classes.metaItem}>
                  <span className={classes.metaIcon}>⏳</span>
                  <span>Responds within 24 hours</span>
                </div>
              </motion.div>
            </div>

            <motion.div
              className={classes.profileImageContainer}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className={classes.profileImageWrapper}>
                <img
                  src={artistByUsername?.displayPictureUrl}
                  alt={fullName}
                  className={classes.profileImage}
                />
                <div className={classes.imageGlow}></div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            className={classes.bookButtonContainer}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <button
              className={classes.bookButton}
              onClick={toggleBookingModal}
              disabled={true}
            >
              <span className={classes.bookButtonIcon}>✦</span>
              Book {fullName.split(" ")[0]} for Your Event
              <span className={classes.comingSoon}>(Coming Soon)</span>
            </button>
          </motion.div>
        </div>

        {/* Main Content */}
        <div className={classes.mainContent}>
          <div className={classes.tabsContainer}>
            {["about", "photos", "videos", "reviews"].map((tab) => (
              <div
                key={tab}
                className={`${classes.tab} ${
                  activeTab === tab ? classes.activeTab : ""
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {capitalize(tab)}
                {activeTab === tab && (
                  <motion.div
                    layoutId="activeIndicator"
                    className={classes.activeIndicator}
                    initial={false}
                    transition={{ duration: 0.3 }}
                  ></motion.div>
                )}
              </div>
            ))}
          </div>

          <div className={classes.contentContainer}>
            {activeTab === "about" && (
              <motion.div
                className={classes.aboutSection}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className={classes.aboutContent}>
                  <div className={classes.sectionHeader}>
                    <h2>About {fullName}</h2>
                    <div className={classes.sectionDivider}></div>
                  </div>

                  <div className={classes.aboutBio}>
                    <p>{about || "No bio available."}</p>
                  </div>

                  <div className={classes.artistDetails}>
                    <div className={classes.detailsColumn}>
                      <div className={classes.detailItem}>
                        <div className={classes.detailHeader}>
                          <div className={classes.detailIcon}>🎨</div>
                          <h3>Category</h3>
                        </div>
                        <p>{category}</p>
                      </div>

                      <div className={classes.detailItem}>
                        <div className={classes.detailHeader}>
                          <div className={classes.detailIcon}>🎵</div>
                          <h3>Genre</h3>
                        </div>
                        <p>{genre}</p>
                      </div>
                    </div>

                    <div className={classes.detailsColumn}>
                      <div className={classes.detailItem}>
                        <div className={classes.detailHeader}>
                          <div className={classes.detailIcon}>💫</div>
                          <h3>Things I Enjoy</h3>
                        </div>
                        <p>
                          {artistByUsername?.things_i_enjoy?.join(", ") ||
                            "Not specified"}
                        </p>
                      </div>

                      <div className={classes.detailItem}>
                        <div className={classes.detailHeader}>
                          <div className={classes.detailIcon}>🎭</div>
                          <h3>Performance Preference</h3>
                        </div>
                        <p>
                          {artistByUsername?.where_do_love_performing?.join(
                            ", "
                          ) || "Not specified"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === "photos" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className={classes.photosSection}
              >
                <div className={classes.sectionHeader}>
                  <h2>Photo Gallery</h2>
                  <div className={classes.sectionDivider}></div>
                </div>
                <ArtistPhotoCarousel
                  photos={photos?.length > 0 ? photos : []}
                />
              </motion.div>
            )}

            {activeTab === "videos" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className={classes.videosSection}
              >
                <div className={classes.sectionHeader}>
                  <h2>Video Showcase</h2>
                  <div className={classes.sectionDivider}></div>
                </div>
                <ArtistVideoCarousel
                  videos={videos?.length > 0 ? videos : []}
                />
              </motion.div>
            )}

            {activeTab === "reviews" && (
              <motion.div
                className={classes.reviewsSection}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className={classes.sectionHeader}>
                  <h2>Customer Reviews</h2>
                  <div className={classes.sectionDivider}></div>
                </div>

                {artistByUsername?.reviews &&
                artistByUsername?.reviews?.length > 0 ? (
                  <div className={classes.reviewsList}>
                    {artistByUsername?.reviews?.map((review, index) => (
                      <motion.div
                        key={index}
                        className={classes.reviewCard}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                      >
                        <div className={classes.reviewHeader}>
                          <div className={classes.reviewerInfo}>
                            <div className={classes.reviewerInitial}>
                              {review.reviewerName.charAt(0)}
                            </div>
                            <div>
                              <h4>{review.reviewerName}</h4>
                              <div className={classes.reviewDate}>
                                {review.date}
                              </div>
                            </div>
                          </div>
                          <div className={classes.reviewRating}>
                            {"★".repeat(review.rating)}
                            {"☆".repeat(5 - review.rating)}
                          </div>
                        </div>
                        <p className={classes.reviewText}>{review.comment}</p>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <motion.div
                    className={classes.emptyReviews}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <div className={classes.emptyReviewsIcon}>✧</div>
                    <p>No reviews yet. Be the first to review after booking!</p>
                    <button
                      className={classes.bookNowBtn}
                      onClick={toggleBookingModal}
                      disabled={true}
                    >
                      Book {fullName.split(" ")[0]} Now
                    </button>
                  </motion.div>
                )}
              </motion.div>
            )}
          </div>
        </div>

        {/* Floating Action Button for quick booking */}
        <div
          className={classes.floatingActionButton}
          // onClick={toggleBookingModal}
        >
          <span>Book</span>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ArtistPreviewProfile;
